// =============================================================================
// Public Webinar Session Page — Registration + Program (No Auth Required)
// =============================================================================
import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { webinarSessionAPI } from '@/lib/api';
import { Calendar, Clock, Users, User, Send, CheckCircle, AlertCircle, MapPin, Tag, ExternalLink } from 'lucide-react';

export default function PublicWebinarSessionPage() {
  const { slug } = useParams();
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState(null);

  const [form, setForm] = useState({
    full_name: '',
    email: '',
    phone: '',
    company: '',
    job_title: '',
    message: '',
  });

  useEffect(() => {
    loadSession();
  }, [slug]);

  const loadSession = async () => {
    try {
      const { data } = await webinarSessionAPI.getPublic(slug);
      setSession(data);
    } catch {
      setError('Session introuvable ou non disponible');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.full_name.trim() || !form.email.trim()) {
      setFormError('Le nom et l\'email sont obligatoires');
      return;
    }
    setSubmitting(true);
    setFormError(null);
    try {
      await webinarSessionAPI.registerPublic(slug, form);
      setSubmitted(true);
    } catch (err) {
      setFormError(err.response?.data?.detail || 'Erreur lors de l\'inscription');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0e1a] flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-cyan-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#0a0e1a] flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-white mb-2">Session non trouvée</h1>
          <p className="text-zinc-400">{error}</p>
        </div>
      </div>
    );
  }

  const schedule = session?.schedule || {};
  const startDate = schedule.start_at ? new Date(schedule.start_at) : null;

  return (
    <div className="min-h-screen bg-[#0a0e1a]">
      {/* Gradient background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[600px] bg-gradient-to-b from-cyan-500/8 via-violet-500/5 to-transparent rounded-full blur-3xl" />
      </div>

      <div className="relative max-w-5xl mx-auto px-4 py-12">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-cyan-500/10 border border-cyan-500/20 text-cyan-400 text-sm font-medium mb-6">
            <Calendar className="w-4 h-4" /> Webinaire
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4 leading-tight">{session.title}</h1>
          {session.description && (
            <p className="text-lg text-zinc-400 max-w-2xl mx-auto leading-relaxed">{session.description}</p>
          )}

          {/* Meta info */}
          <div className="flex flex-wrap items-center justify-center gap-6 mt-8 text-sm text-zinc-400">
            {startDate && (
              <span className="flex items-center gap-2 bg-zinc-900/60 px-4 py-2 rounded-xl border border-zinc-800">
                <Calendar className="w-4 h-4 text-cyan-400" />
                {startDate.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
              </span>
            )}
            {startDate && (
              <span className="flex items-center gap-2 bg-zinc-900/60 px-4 py-2 rounded-xl border border-zinc-800">
                <Clock className="w-4 h-4 text-violet-400" />
                {startDate.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })} • {schedule.duration_minutes || 60} min
              </span>
            )}
            <span className="flex items-center gap-2 bg-zinc-900/60 px-4 py-2 rounded-xl border border-zinc-800">
              <Users className="w-4 h-4 text-emerald-400" />
              {session.max_participants} places
            </span>
            {session.created_by_name && (
              <span className="flex items-center gap-2 bg-zinc-900/60 px-4 py-2 rounded-xl border border-zinc-800">
                <User className="w-4 h-4 text-amber-400" />
                {session.created_by_name}
              </span>
            )}
          </div>

          {/* Tags */}
          {session.tags?.length > 0 && (
            <div className="flex flex-wrap items-center justify-center gap-2 mt-4">
              {session.tags.map((tag, i) => (
                <span key={i} className="flex items-center gap-1 px-3 py-1 rounded-full bg-violet-500/10 border border-violet-500/20 text-violet-300 text-xs">
                  <Tag className="w-3 h-3" /> {tag}
                </span>
              ))}
            </div>
          )}
        </div>

        <div className="grid md:grid-cols-5 gap-8">
          {/* Program — left side */}
          <div className="md:col-span-3">
            {session.program?.length > 0 && (
              <div className="bg-zinc-900/60 rounded-2xl border border-zinc-800 p-6 backdrop-blur">
                <h2 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
                  📋 Programme
                </h2>
                <div className="space-y-4">
                  {session.program.map((item, i) => (
                    <div key={i} className="flex gap-4 group">
                      <div className="flex flex-col items-center">
                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-cyan-500 to-violet-600 flex items-center justify-center text-white text-xs font-bold shrink-0">
                          {i + 1}
                        </div>
                        {i < session.program.length - 1 && (
                          <div className="w-px flex-1 bg-gradient-to-b from-violet-600/40 to-transparent mt-2" />
                        )}
                      </div>
                      <div className="pb-6 flex-1">
                        <h3 className="text-sm font-semibold text-white group-hover:text-cyan-400 transition-colors">{item.title}</h3>
                        <div className="flex items-center gap-3 mt-1 text-xs text-zinc-500">
                          {item.speaker && <span className="flex items-center gap-1"><User className="w-3 h-3" /> {item.speaker}</span>}
                          {item.duration_minutes && <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {item.duration_minutes} min</span>}
                        </div>
                        {item.description && (
                          <p className="text-xs text-zinc-400 mt-2 leading-relaxed">{item.description}</p>
                        )}
                        {item.link_url && (
                          <a href={item.link_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 mt-3 text-xs text-cyan-400 hover:text-cyan-300 transition-colors bg-cyan-500/10 border border-cyan-500/20 px-3 py-1.5 rounded-lg w-fit">
                            <ExternalLink className="w-3.5 h-3.5" />
                            {item.link_text || 'Voir le lien'}
                          </a>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Registration form — right side */}
          <div className="md:col-span-2">
            <div className="bg-zinc-900/60 rounded-2xl border border-zinc-800 p-6 backdrop-blur sticky top-8">
              {submitted ? (
                <div className="text-center py-8">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center">
                    <CheckCircle className="w-8 h-8 text-emerald-400" />
                  </div>
                  <h3 className="text-lg font-bold text-white mb-2">Inscription enregistrée !</h3>
                  <p className="text-sm text-zinc-400 leading-relaxed">
                    Votre inscription est en attente de validation.
                    Vous recevrez un email de confirmation avec le lien d'accès au webinaire.
                  </p>
                </div>
              ) : (
                <>
                  <h2 className="text-lg font-bold text-white mb-1">S'inscrire</h2>
                  <p className="text-xs text-zinc-500 mb-5">Remplissez le formulaire pour réserver votre place</p>

                  {formError && (
                    <div className="mb-4 p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-xs flex items-center gap-2">
                      <AlertCircle className="w-4 h-4 shrink-0" /> {formError}
                    </div>
                  )}

                  <form onSubmit={handleSubmit} className="space-y-3">
                    <div>
                      <label className="text-xs text-zinc-400 block mb-1">Nom complet *</label>
                      <input
                        value={form.full_name}
                        onChange={(e) => setForm(prev => ({ ...prev, full_name: e.target.value }))}
                        placeholder="Jean Dupont"
                        className="w-full h-10 px-3 rounded-lg bg-zinc-800/80 border border-zinc-700 text-white text-sm placeholder:text-zinc-600 focus:outline-none focus:ring-2 focus:ring-cyan-500/40 focus:border-cyan-500/60 transition-all"
                        required
                      />
                    </div>
                    <div>
                      <label className="text-xs text-zinc-400 block mb-1">Email *</label>
                      <input
                        type="email"
                        value={form.email}
                        onChange={(e) => setForm(prev => ({ ...prev, email: e.target.value }))}
                        placeholder="jean@example.com"
                        className="w-full h-10 px-3 rounded-lg bg-zinc-800/80 border border-zinc-700 text-white text-sm placeholder:text-zinc-600 focus:outline-none focus:ring-2 focus:ring-cyan-500/40 focus:border-cyan-500/60 transition-all"
                        required
                      />
                    </div>
                    <div>
                      <label className="text-xs text-zinc-400 block mb-1">Téléphone</label>
                      <input
                        value={form.phone}
                        onChange={(e) => setForm(prev => ({ ...prev, phone: e.target.value }))}
                        placeholder="+33 6 00 00 00 00"
                        className="w-full h-10 px-3 rounded-lg bg-zinc-800/80 border border-zinc-700 text-white text-sm placeholder:text-zinc-600 focus:outline-none focus:ring-2 focus:ring-cyan-500/40 focus:border-cyan-500/60 transition-all"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-zinc-400 block mb-1">Entreprise</label>
                      <input
                        value={form.company}
                        onChange={(e) => setForm(prev => ({ ...prev, company: e.target.value }))}
                        placeholder="Nom de l'entreprise"
                        className="w-full h-10 px-3 rounded-lg bg-zinc-800/80 border border-zinc-700 text-white text-sm placeholder:text-zinc-600 focus:outline-none focus:ring-2 focus:ring-cyan-500/40 focus:border-cyan-500/60 transition-all"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-zinc-400 block mb-1">Poste</label>
                      <input
                        value={form.job_title}
                        onChange={(e) => setForm(prev => ({ ...prev, job_title: e.target.value }))}
                        placeholder="Développeur, DevOps..."
                        className="w-full h-10 px-3 rounded-lg bg-zinc-800/80 border border-zinc-700 text-white text-sm placeholder:text-zinc-600 focus:outline-none focus:ring-2 focus:ring-cyan-500/40 focus:border-cyan-500/60 transition-all"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-zinc-400 block mb-1">Message (optionnel)</label>
                      <textarea
                        value={form.message}
                        onChange={(e) => setForm(prev => ({ ...prev, message: e.target.value }))}
                        placeholder="Une question, motivation..."
                        rows={3}
                        className="w-full px-3 py-2 rounded-lg bg-zinc-800/80 border border-zinc-700 text-white text-sm placeholder:text-zinc-600 focus:outline-none focus:ring-2 focus:ring-cyan-500/40 focus:border-cyan-500/60 transition-all resize-none"
                      />
                    </div>
                    <button
                      type="submit"
                      disabled={submitting}
                      className="w-full h-11 rounded-xl bg-gradient-to-r from-cyan-500 to-violet-600 hover:from-cyan-600 hover:to-violet-700 text-white font-semibold text-sm flex items-center justify-center gap-2 transition-all disabled:opacity-50 disabled:cursor-not-allowed mt-2"
                    >
                      <Send className="w-4 h-4" />
                      {submitting ? 'Inscription en cours...' : 'S\'inscrire'}
                    </button>
                  </form>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="text-center mt-16 pb-8">
          <p className="text-xs text-zinc-600">Propulsé par <a href="https://takapes.info" className="text-cyan-500/60 hover:text-cyan-400 transition-colors">Takapes Learn</a></p>
        </div>
      </div>
    </div>
  );
}
